import useReveal from '../hooks/useReveal';

const TIMELINE = [
  { year:'2018', icon:'🌱', title:'The Research Phase', desc:'6 months of field research visiting 50+ kirana shops across Hyderabad, documenting every pain point in the wholesale grocery supply chain.' },
  { year:'2020', icon:'🚀', title:'Launch Day',         desc:'Launched with 12 pilot kirana shops in Secunderabad. First delivery van hit the road with 40 SKUs and a WhatsApp number.' },
  { year:'2021', icon:'📈', title:'Growing Fast',       desc:'Crossed 100 regular customers. Expanded product catalogue to 80+ SKUs. Added a second delivery van to cover south Hyderabad.' },
  { year:'2022', icon:'🏭', title:'200+ Shops',         desc:'Serving over 200 kirana stores. Introduced digital GST invoicing and launched our dedicated warehouse in Sanathnagar.' },
  { year:'2024', icon:'🏆', title:'500+ Partners',      desc:'Reached 500+ active B2B customers. 200+ product catalogue. GPS-tracked fleet of vans. Hyderabad\'s most trusted wholesale delivery partner.' },
];

const VALUES = [
  { icon:'🤝', title:'Trust First',    desc:'We build lasting relationships based on transparency, consistency and reliability in every single delivery.' },
  { icon:'🌿', title:'Quality Always', desc:'Every product is freshness-checked before dispatch. We never compromise on quality — ever.' },
  { icon:'⏰', title:'Time is Money',  desc:'We respect that your time is precious. On-time delivery is our standard, not just a promise.' },
  { icon:'📊', title:'Fair Pricing',   desc:'Genuine wholesale rates with no hidden markups. What you see on the invoice is what you pay, always.' },
];

const INFRA = [
  { icon:'🏭', title:'10,000 sq ft Warehouse',  desc:'Our large Sanathnagar warehouse is fully stocked with 200+ products at all times. Organised by category for fast order picking.' },
  { icon:'🚐', title:'GPS-Tracked Fleet',        desc:'Branded delivery vans equipped with GPS tracking. Real-time location shared with customers. Route-optimised for fastest delivery.' },
  { icon:'📦', title:'Quality Control System',   desc:'Every item is weight-checked and quality-verified before dispatch. Barcode-based inventory management ensures zero wrong-item deliveries.' },
  { icon:'📱', title:'WhatsApp Order System',    desc:'Simple WhatsApp-based ordering that works for everyone — no tech skills needed. Our team confirms orders within 5 minutes.' },
  { icon:'📄', title:'Digital GST Invoicing',    desc:'Automated GST invoice generation for every order. Printed copy given on delivery. Digital copies emailed on request.' },
  { icon:'🔄', title:'Replacement Policy',       desc:'Any damaged, wrong or substandard item replaced on the very next delivery — no questions asked. We stand behind every product.' },
];

export default function AboutPage() {
  useReveal();
  return (
    <div>
      {/* Hero */}
      <div className="page-hero">
        <div className="page-hero-inner">
          <div className="breadcrumb"><span>Home</span><span className="sep">/</span><span className="active">About Us</span></div>
          <h1>About eShop Global</h1>
          <p style={{ color:'rgba(255,255,255,.75)', fontSize:'1.05rem', lineHeight:1.8, maxWidth:600 }}>
            Hyderabad's trusted B2B wholesale grocery delivery partner, serving kirana stores, restaurants and provision shops since 2020.
          </p>
        </div>
      </div>

      {/* Story section */}
      <section style={{ padding:'96px 5vw', background:'#fff' }}>
        <div className="container">
          <div style={{ display:'grid', gap:64, alignItems:'center' }} className="story-grid">
            <div className="reveal-left">
              <div className="tag-line">Our Story</div>
              <h2 className="section-title">From a Simple Vision to <em>Hyderabad's Partner</em></h2>
              <p style={{ fontSize:'.97rem', lineHeight:1.9, color:'var(--text-mid)', marginBottom:18 }}>
                eShop Global was born out of a simple observation: kirana shop owners in Hyderabad were losing 2–3 hours every morning making trips to Begum Bazaar mandi — fighting traffic, negotiating with middlemen, loading heavy bags into autos.
              </p>
              <p style={{ fontSize:'.97rem', lineHeight:1.9, color:'var(--text-mid)', marginBottom:32 }}>
                We set out to change that. By building a direct, technology-powered wholesale delivery system, we bring the mandi to the shop owner's doorstep — at lower prices, with guaranteed quality, proper GST invoices, and a friendly smile.
              </p>
              <div style={{ display:'flex', gap:40 }}>
                {[['500+','Shops Served'],['200+','Products'],['6+','Years']].map(([n,l],i)=>(
                  <div key={i} style={{ textAlign:'center' }}>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:'2rem', fontWeight:800, color:'var(--orange)', lineHeight:1 }}>{n}</div>
                    <div style={{ fontSize:'.77rem', color:'var(--text-light)', fontWeight:600, marginTop:4 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Photo collage */}
            <div className="reveal-right" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div style={{ gridColumn:'1/-1', height:240, borderRadius:12, overflow:'hidden' }}>
                <img src="https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&q=80&fit=crop" alt="Warehouse" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              </div>
              <div style={{ height:160, borderRadius:12, overflow:'hidden' }}>
                <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&q=80&fit=crop" alt="Delivery van" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              </div>
              <div style={{ height:160, borderRadius:12, overflow:'hidden' }}>
                <img src="https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&q=80&fit=crop" alt="Rice products" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              </div>
            </div>
          </div>
          <style>{`.story-grid{grid-template-columns:1fr 1fr!important;} @media(max-width:768px){.story-grid{grid-template-columns:1fr!important;}}`}</style>
        </div>
      </section>

      {/* Numbers bar */}
      <section style={{ padding:'0 5vw 96px', background:'#fff' }}>
        <div className="container">
          <div style={{ display:'grid', background:'#1A0F05', borderRadius:12, overflow:'hidden', boxShadow:'var(--shadow-lg)' }} className="reveal nums-grid">
            {[{n:'500+',label:'Kirana Stores',sub:'Active B2B customers'},{n:'200+',label:'Products',sub:'In our catalogue'},{n:'6+',label:'Years',sub:'Of experience'},{n:'100%',label:'GST Invoices',sub:'On every delivery'}].map((s,i)=>(
              <div key={i} style={{ padding:'36px 24px', textAlign:'center', borderRight:i<3?'1px solid rgba(255,255,255,.1)':'none' }}>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:'2.4rem', fontWeight:800, color:'var(--orange-light)', lineHeight:1 }}>{s.n}</div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:'1rem', fontWeight:600, color:'#fff', marginTop:8 }}>{s.label}</div>
                <div style={{ fontSize:'.75rem', color:'rgba(255,255,255,.45)', marginTop:4 }}>{s.sub}</div>
              </div>
            ))}
          </div>
          <style>{`.nums-grid{grid-template-columns:repeat(4,1fr)!important;} @media(max-width:700px){.nums-grid{grid-template-columns:repeat(2,1fr)!important;}}`}</style>
        </div>
      </section>

      {/* Timeline */}
      <section style={{ padding:'96px 5vw', background:'var(--cream)' }}>
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:64 }} className="reveal">
            <div className="tag-line" style={{ justifyContent:'center' }}>Our Journey</div>
            <h2 className="section-title" style={{ textAlign:'center', margin:'0 auto' }}>Our <em>Timeline & Story</em></h2>
          </div>
          <div style={{ maxWidth:820, margin:'0 auto', position:'relative' }}>
            <div style={{ position:'absolute', left:'50%', top:0, bottom:0, width:2, background:'linear-gradient(to bottom, var(--orange), #FF7D2A, var(--orange))', transform:'translateX(-50%)' }} className="hide-m"/>
            {TIMELINE.map((item,i)=>(
              <div key={i} className="reveal" style={{ display:'flex', gap:28, marginBottom:52, alignItems:'flex-start', flexDirection:i%2===0?'row':'row-reverse', animationDelay:`${i*.1}s` }}>
                <div style={{ flex:1 }} className="hide-m">
                  {i%2===0?(
                    <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:10, padding:'22px 26px', boxShadow:'var(--shadow-sm)', textAlign:'right' }}>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:'1.5rem', fontWeight:800, color:'var(--orange)', marginBottom:6 }}>{item.year}</div>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, color:'var(--dark)', fontSize:'1rem', marginBottom:8 }}>{item.title}</div>
                      <p style={{ fontSize:'.86rem', color:'var(--text-mid)', lineHeight:1.78 }}>{item.desc}</p>
                    </div>
                  ):<div/>}
                </div>
                <div style={{ width:52, height:52, borderRadius:'50%', background:i===4?'var(--orange)':'#1A0F05', border:'4px solid #fff', boxShadow:'var(--shadow-sm)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'1.1rem', zIndex:2 }}>{item.icon}</div>
                <div style={{ flex:1 }} className="hide-m">
                  {i%2!==0?(
                    <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:10, padding:'22px 26px', boxShadow:'var(--shadow-sm)' }}>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:'1.5rem', fontWeight:800, color:'var(--orange)', marginBottom:6 }}>{item.year}</div>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, color:'var(--dark)', fontSize:'1rem', marginBottom:8 }}>{item.title}</div>
                      <p style={{ fontSize:'.86rem', color:'var(--text-mid)', lineHeight:1.78 }}>{item.desc}</p>
                    </div>
                  ):<div/>}
                </div>
                <div className="show-m" style={{ flex:1, background:'#fff', border:'1px solid var(--border)', borderRadius:10, padding:'18px 20px' }}>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:'1.15rem', fontWeight:700, color:'var(--orange)' }}>{item.year} — {item.title}</div>
                  <p style={{ fontSize:'.85rem', color:'var(--text-mid)', marginTop:6, lineHeight:1.75 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section style={{ padding:'96px 5vw', background:'#fff' }}>
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:56 }} className="reveal">
            <div className="tag-line" style={{ justifyContent:'center' }}>Our Values</div>
            <h2 className="section-title" style={{ textAlign:'center', margin:'0 auto' }}>What We <em>Believe In</em></h2>
          </div>
          <div style={{ display:'grid', gap:22 }} className="values-grid">
            {VALUES.map((v,i)=>(
              <div key={i} className="reveal" style={{ textAlign:'center', padding:'36px 24px', background:'var(--cream)', borderRadius:10, border:'1px solid var(--border)', animationDelay:`${i*.1}s` }}>
                <div style={{ fontSize:'2.4rem', marginBottom:16 }}>{v.icon}</div>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, color:'var(--dark)', fontSize:'1.08rem', marginBottom:10 }}>{v.title}</h3>
                <p style={{ fontSize:'.86rem', color:'var(--text-mid)', lineHeight:1.82 }}>{v.desc}</p>
              </div>
            ))}
          </div>
          <style>{`.values-grid{grid-template-columns:repeat(4,1fr)!important;} @media(max-width:900px){.values-grid{grid-template-columns:repeat(2,1fr)!important;}} @media(max-width:540px){.values-grid{grid-template-columns:1fr!important;}}`}</style>
        </div>
      </section>

      {/* Infrastructure with real photos */}
      <section style={{ padding:'96px 5vw', background:'var(--cream)' }}>
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:56 }} className="reveal">
            <div className="tag-line" style={{ justifyContent:'center' }}>Our Infrastructure</div>
            <h2 className="section-title" style={{ textAlign:'center', margin:'0 auto' }}>Built for <em>Scale & Speed</em></h2>
          </div>
          <div style={{ display:'grid', gap:22 }} className="infra-grid">
            {INFRA.map((item,i)=>(
              <div key={i} className="reveal" style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:10, padding:'28px 24px', transition:'transform .3s, box-shadow .3s', animationDelay:`${i*.08}s` }}
                onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='var(--shadow-md)'; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
                <div style={{ fontSize:'2rem', marginBottom:14 }}>{item.icon}</div>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:'1.05rem', fontWeight:700, color:'var(--dark)', marginBottom:10 }}>{item.title}</h3>
                <p style={{ fontSize:'.86rem', color:'var(--text-mid)', lineHeight:1.82 }}>{item.desc}</p>
              </div>
            ))}
          </div>
          <style>{`.infra-grid{grid-template-columns:repeat(3,1fr)!important;} @media(max-width:900px){.infra-grid{grid-template-columns:repeat(2,1fr)!important;}} @media(max-width:540px){.infra-grid{grid-template-columns:1fr!important;}}`}</style>
        </div>
      </section>
    </div>
  );
}
